import React, { useState } from 'react';
import { copyToClipboard } from '../utils/constants';

// Copy icon component
const CopyIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg 
    className={`w-4 h-4 ${className}`} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2} 
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" 
    />
  </svg>
);

// Check icon component
const CheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg 
    className={`w-4 h-4 ${className}`} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2} 
      d="M5 13l4 4L19 7" 
    />
  </svg>
);

interface OutputSectionProps {
  originalText: string;
  transformedText: string;
  isFromCache: boolean;
  isLoading: boolean;
  provider?: string;
}

const OutputSection: React.FC<OutputSectionProps> = ({
  originalText,
  transformedText,
  isFromCache,
  isLoading,
  provider,
}) => {
  const [copiedOriginal, setCopiedOriginal] = useState(false);
  const [copiedTransformed, setCopiedTransformed] = useState(false);

  const handleCopy = async (text: string, type: 'original' | 'transformed') => {
    const success = await copyToClipboard(text);
    if (success) {
      if (type === 'original') {
        setCopiedOriginal(true);
        setTimeout(() => setCopiedOriginal(false), 2000);
      } else {
        setCopiedTransformed(true);
        setTimeout(() => setCopiedTransformed(false), 2000);
      }
    }
  };

  if (!originalText && !transformedText && !isLoading) {
    return null;
  }

  return (
    <div className="mt-4 space-y-3 flex-1 flex flex-col min-h-0">
      {isFromCache && (
        <div className="bg-neo-green border-4 border-neo-black p-2 shadow-neo-sm">
          <p className="text-neo-black font-medium text-sm">✨ Result from cache - faster response!</p>
        </div>
      )}

      {provider && !isFromCache && (
        <div className="bg-neo-blue border-4 border-neo-black p-2 shadow-neo-sm">
          <p className="text-neo-black font-medium text-sm">
            🤖 Powered by {provider}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
        {/* Original Text */}
        <div className="bg-neo-white border-4 border-neo-black shadow-neo flex flex-col min-h-0">
          <div className="bg-neo-blue border-b-4 border-neo-black px-3 py-2 flex justify-between items-center flex-shrink-0">
            <h3 className="text-lg font-bold text-neo-black">Original Text</h3>
            {originalText && (
              <button
                onClick={() => handleCopy(originalText, 'original')}
                className="p-2 bg-neo-yellow border-3 border-neo-black hover:shadow-neo-sm transition-all duration-200 active:shadow-none active:translate-x-1 active:translate-y-1 flex items-center justify-center"
                title={copiedOriginal ? 'Copied!' : 'Copy to clipboard'}
              >
                {copiedOriginal ? <CheckIcon className="text-green-600" /> : <CopyIcon />}
              </button>
            )}
          </div>
          <div className="p-3 flex-1 overflow-auto min-h-0">
            {originalText ? (
              <p className="text-neo-black leading-relaxed whitespace-pre-wrap text-base">
                {originalText}
              </p>
            ) : (
              <p className="text-gray-500 italic text-sm">Your original text will appear here...</p>
            )}
          </div>
        </div>

        {/* Transformed Text */}
        <div className="bg-neo-white border-4 border-neo-black shadow-neo flex flex-col min-h-0">
          <div className="bg-neo-purple border-b-4 border-neo-black px-3 py-2 flex justify-between items-center flex-shrink-0">
            <h3 className="text-lg font-bold text-neo-black">Eloquent English</h3>
            {transformedText && !isLoading && (
              <button
                onClick={() => handleCopy(transformedText, 'transformed')}
                className="p-2 bg-neo-yellow border-3 border-neo-black hover:shadow-neo-sm transition-all duration-200 active:shadow-none active:translate-x-1 active:translate-y-1 flex items-center justify-center"
                title={copiedTransformed ? 'Copied!' : 'Copy to clipboard'}
              >
                {copiedTransformed ? <CheckIcon className="text-green-600" /> : <CopyIcon />}
              </button>
            )}
          </div>
          <div className="p-3 flex-1 overflow-auto min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-neo-black border-t-neo-yellow rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-neo-black font-medium text-sm">Crafting eloquent prose...</p>
                </div>
              </div>
            ) : transformedText ? (
              <p className="text-neo-black leading-relaxed whitespace-pre-wrap text-base">
                {transformedText}
              </p>
            ) : (
              <p className="text-gray-500 italic text-sm">Your transformed text will appear here...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutputSection;
